import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../App';
import { supabase } from '../supabaseClient';
import { buscarDadosZARC, listarCulturasZARC } from '../services/zarcService';
import { DadosMeteorologicosService } from '../services/DadosMeteorologicosService';
import CalendarioZARC from '../components/CalendarioZARC';
import DisclaimerInfo from '../components/DisclaimerInfo';
import './InfoAdicionais.css';

// Componente auxiliar para mostrar decêndios detalhados
function DecendiosDetalhados({ decendios, cor, isMobile }) {
  if (!decendios) return null;
  
  const listaDecendios = decendios.split(',').map(Number).filter(n => !isNaN(n));
  
  // Função para converter decêndio em data
  const decendioParaData = (dec) => {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    
    const mesIndex = Math.floor((dec - 1) / 3);
    const decendioNoMes = ((dec - 1) % 3) + 1;
    
    if (mesIndex < 0 || mesIndex >= 12) return null;
    
    const mes = meses[mesIndex];
    const mesAbrev = mes.substring(0, 3);
    
    let diaInicio, diaFim;
    if (decendioNoMes === 1) {
      diaInicio = 1;
      diaFim = 10;
    } else if (decendioNoMes === 2) {
      diaInicio = 11;
      diaFim = 20;
    } else {
      diaInicio = 21;
      const diasNoMes = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      diaFim = diasNoMes[mesIndex];
    }
    
    return {
      dec,
      mes,
      mesAbrev,
      diaInicio,
      diaFim,
      descricao: `${mesAbrev} ${diaInicio}-${diaFim}`
    };
  };
  
  return (
    <div style={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: 6 
    }}>
      {listaDecendios.map(dec => {
        const info = decendioParaData(dec);
        if (!info) return null;
        
        return (
          <div
            key={dec}
            style={{
              background: 'white',
              border: `2px solid ${cor}`,
              borderRadius: 6,
              padding: isMobile ? '6px 10px' : '8px 12px',
              fontSize: isMobile ? 11 : 12,
              fontWeight: 600,
              color: cor,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ fontSize: isMobile ? 9 : 10, opacity: 0.8 }}>
              Decêndio {dec}
            </div>
            <div>
              {info.descricao}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InfoAdicionais({ talhao }) {
  const { darkMode } = useContext(ThemeContext);
  const [altitude, setAltitude] = useState(null);
  const [loadingAltitude, setLoadingAltitude] = useState(false);
  const [etoProjeto, setEtoProjeto] = useState(null);
  const [loadingEto, setLoadingEto] = useState(false);
  const [dadosClimaticos, setDadosClimaticos] = useState(null);
  const [loadingClima, setLoadingClima] = useState(false);
  const [erroClima, setErroClima] = useState(null);
  const [tentativaClima, setTentativaClima] = useState(0);
  const [dadosKoppen, setDadosKoppen] = useState(null);
  const [loadingKoppen, setLoadingKoppen] = useState(false);
  const [codeMuni, setCodeMuni] = useState(null);
  const [dadosZARC, setDadosZARC] = useState(null);
  const [loadingZARC, setLoadingZARC] = useState(false);
  const [culturasDisponiveis, setCulturasDisponiveis] = useState([]);
  const [expandido, setExpandido] = useState({
    resumo: true,
    geolocalizacao: true,
    clima: true,
    zoneamento: false
  });

  // Detectar se é mobile (reativo a resize)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Buscar code_muni da propriedade do talhão
  useEffect(() => {
    async function fetchCodeMuni() {
      if (!talhao?.propriedade_id) return;
      
      try {
        const { data, error } = await supabase
          .from('propriedades')
          .select('code_muni')
          .eq('id', talhao.propriedade_id)
          .single();
        
        if (!error && data?.code_muni) {
          console.log('📍 Code_muni encontrado:', data.code_muni);
          setCodeMuni(data.code_muni);
        } else {
          console.log('⚠️ Code_muni não encontrado para esta propriedade');
        }
      } catch (error) {
        console.error('Erro ao buscar code_muni:', error);
      }
    }
    
    fetchCodeMuni();
  }, [talhao?.propriedade_id]);

  // Buscar dados climáticos de Köppen quando tiver code_muni
  useEffect(() => {
    async function fetchDadosKoppen() {
      if (!codeMuni) return;
      
      setLoadingKoppen(true);
      try {
        console.log('🌍 Buscando dados Köppen para code_muni:', codeMuni);
        
        const { data, error } = await supabase
          .from('clima_koppen_data')
          .select('*')
          .eq('code_muni', codeMuni)
          .single();
        
        if (!error && data) {
          console.log('✅ Dados Köppen encontrados:', data);
          setDadosKoppen(data);
        } else {
          console.log('⚠️ Dados Köppen não encontrados para este município');
          setDadosKoppen(null);
        }
      } catch (error) {
        console.error('Erro ao buscar dados Köppen:', error);
        setDadosKoppen(null);
      } finally {
        setLoadingKoppen(false);
      }
    }
    
    fetchDadosKoppen();
  }, [codeMuni]);

  // Buscar dados ZARC quando tiver code_muni e cultura
  useEffect(() => {
    async function fetchDadosZARC() {
      if (!codeMuni || !talhao?.cultura) return;
      
      setLoadingZARC(true);
      try {
        console.log('🌱 Buscando dados ZARC para:', talhao.cultura, 'em', codeMuni);
        
        const dados = await buscarDadosZARC(codeMuni, talhao.cultura);
        
        if (dados) {
          console.log('✅ Dados ZARC encontrados:', dados);
          setDadosZARC(dados);
          setCulturasDisponiveis([]);
        } else {
          console.log('ℹ️ Dados ZARC não disponíveis para esta cultura');
          setDadosZARC(null);
          setCulturasDisponiveis([]);
          // Não buscar culturas disponíveis - simplesmente não mostra a seção
        }
      } catch (error) {
        console.error('Erro ao buscar dados ZARC:', error);
        setDadosZARC(null);
        setCulturasDisponiveis([]);
      } finally {
        setLoadingZARC(false);
      }
    }
    
    fetchDadosZARC();
  }, [codeMuni, talhao?.cultura]);

  // Buscar altitude via API quando tiver coordenadas
  useEffect(() => {
    async function fetchAltitude() {
      if (!talhao?.coordenada_x || !talhao?.coordenada_y) return;
      
      setLoadingAltitude(true);
      try {
        const response = await fetch(
          `https://api.open-elevation.com/api/v1/lookup?locations=${talhao.coordenada_y},${talhao.coordenada_x}`
        );
        const data = await response.json();
        if (data?.results?.[0]?.elevation) {
          setAltitude(Math.round(data.results[0].elevation));
        }
      } catch (error) {
        console.error('Erro ao buscar altitude:', error);
      } finally {
        setLoadingAltitude(false);
      }
    }
    
    fetchAltitude();
  }, [talhao?.coordenada_x, talhao?.coordenada_y]);

  // Buscar ETO de projeto pela coordenada mais próxima
  useEffect(() => {
    async function fetchEtoProjeto() {
      if (!talhao?.coordenada_x || !talhao?.coordenada_y) return;
      
      setLoadingEto(true);
      try {
        const lat = parseFloat(talhao.coordenada_y);
        const lon = parseFloat(talhao.coordenada_x);
        
        console.log('Buscando ETO para coordenadas:', { lat, lon });
        
        // Busca em um raio aproximado (0.5 graus = ~55km)
        const raio = 0.5;
        const { data, error } = await supabase
          .from('eto_projeto_data')
          .select('"X", "Y", "Eto_projeto"')
          .gte('Y', lat - raio)
          .lte('Y', lat + raio)
          .gte('X', lon - raio)
          .lte('X', lon + raio)
          .limit(50);
        
        if (error) {
          console.error('Erro ao buscar ETO:', error);
          return;
        }
        
        if (data && data.length > 0) {
          console.log(`Encontrados ${data.length} pontos próximos`);
          
          // Calcula distância euclidiana para cada ponto
          const comDistancia = data.map(ponto => {
            const dx = parseFloat(ponto.X) - lon;
            const dy = parseFloat(ponto.Y) - lat;
            const distancia = Math.sqrt(dx * dx + dy * dy);
            return { ...ponto, distancia };
          });
          
          // Ordena por distância e pega o mais próximo
          comDistancia.sort((a, b) => a.distancia - b.distancia);
          const maisProximo = comDistancia[0];
          
          console.log('Ponto mais próximo:', maisProximo);
          
          if (maisProximo) {
            setEtoProjeto(parseFloat(maisProximo.Eto_projeto).toFixed(2));
          }
        } else {
          console.log('Nenhum ponto encontrado no raio especificado');
        }
      } catch (error) {
        console.error('Erro ao buscar ETO de projeto:', error);
      } finally {
        setLoadingEto(false);
      }
    }
    
    fetchEtoProjeto();
  }, [talhao?.coordenada_x, talhao?.coordenada_y]);

  // Buscar a climatologia compartilhada entre Info e Clima do Talhão.
  useEffect(() => {
    let ativo = true;

    async function fetchDadosClimaticos() {
      if (!talhao?.coordenada_x || !talhao?.coordenada_y || !expandido.clima) return;

      const lat = parseFloat(talhao.coordenada_y);
      const lon = parseFloat(talhao.coordenada_x);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

      setLoadingClima(true);
      setErroClima(null);
      try {
        const params = await DadosMeteorologicosService.buscarClimatologia(lon, lat);
        if (ativo && params) {
          
          // Extrair dados mensais
          const meses = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
          const nomeMeses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                            'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
          const diasPorMes = [31, 28.25, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
          const valorValido = valor => Number.isFinite(Number(valor)) && Number(valor) > -900
            ? Number(valor)
            : 0;
          
          const temperaturas = meses.map((m, i) => ({
            mes: nomeMeses[i],
            media: valorValido(params.T2M?.[m]),
            maxima: valorValido(params.T2M_MAX?.[m]),
            minima: valorValido(params.T2M_MIN?.[m])
          }));
          
          const precipitacao = meses.map((m, i) => ({
            mes: nomeMeses[i],
            valor: valorValido(params.PRECTOTCORR?.[m])
          }));
          
          const umidade = meses.map((m, i) => ({
            mes: nomeMeses[i],
            valor: valorValido(params.RH2M?.[m])
          }));
          
          // Calcular estatísticas
          const tempMediaAnual = (temperaturas.reduce((sum, t) => sum + t.media, 0) / 12).toFixed(1);
          const mesMaisFrio = temperaturas.reduce((min, t) => t.media < min.media ? t : min);
          const mesMaisQuente = temperaturas.reduce((max, t) => t.media > max.media ? t : max);
          
          // PRECTOTCORR da climatologia é uma taxa média diária por mês.
          const precipAnual = precipitacao
            .reduce((sum, p, index) => sum + (p.valor * diasPorMes[index]), 0)
            .toFixed(0);
          
          const umidadeMedia = (umidade.reduce((sum, u) => sum + u.valor, 0) / 12).toFixed(0);
          
          const resultado = {
            tempMediaAnual,
            mesMaisFrio: { nome: mesMaisFrio.mes, temp: mesMaisFrio.media.toFixed(1) },
            mesMaisQuente: { nome: mesMaisQuente.mes, temp: mesMaisQuente.media.toFixed(1) },
            precipAnual,
            umidadeMedia,
            temperaturas,
            precipitacao,
            umidade
          };
          
          setDadosClimaticos(resultado);
          console.log('✅ Dados climáticos carregados:', resultado);
        }
      } catch (error) {
        console.error('Erro ao buscar dados climáticos:', error);
        if (ativo) {
          setDadosClimaticos(null);
          setErroClima(error?.message || 'Não foi possível carregar o histórico climático neste momento.');
        }
      } finally {
        if (ativo) setLoadingClima(false);
      }
    }
    
    fetchDadosClimaticos();
    return () => { ativo = false; };
  }, [talhao?.coordenada_x, talhao?.coordenada_y, expandido.clima, tentativaClima]);

  // Função para obter classificação climática de Köppen
  const obterClassificacaoClimatica = () => {
    // Somente retorna se tiver dados da tabela Köppen
    if (dadosKoppen?.koppen) {
      // Mapeia códigos Köppen para descrições completas
      const koppenDescricoes = {
        // Climas Tropicais (A)
        'Af': 'Af - Tropical úmido, chuvas o ano todo',
        'Am': 'Am - Tropical de monção, curta estação seca',
        'Aw': 'Aw - Tropical com estação seca (inverno)',
        'As': 'As - Tropical com estação seca (verão)',
        
        // Climas Áridos (B)
        'BWh': 'BWh - Desértico quente, muito seco',
        'BWk': 'BWk - Desértico frio',
        'BSh': 'BSh - Semiárido quente',
        'BSk': 'BSk - Semiárido frio',
        
        // Climas Temperados (C)
        'Cfa': 'Cfa - Subtropical úmido, verão quente',
        'Cfb': 'Cfb - Temperado oceânico, verão ameno',
        'Cwa': 'Cwa - Temperado com inverno seco e verão quente',
        'Cwb': 'Cwb - Temperado de altitude, inverno seco',
        'Cfc': 'Cfc - Temperado oceânico frio',
        'Csa': 'Csa - Mediterrâneo, verão quente',
        'Csb': 'Csb - Mediterrâneo, verão ameno',
        'Csc': 'Csc - Mediterrâneo frio',
        
        // Climas Continentais (D)
        'Dfa': 'Dfa - Continental, verão quente',
        'Dfb': 'Dfb - Continental, verão ameno',
        'Dwa': 'Dwa - Continental com inverno seco',
        'Dwb': 'Dwb - Continental com inverno seco e verão ameno',
        'Dfc': 'Dfc - Subártico',
        'Dfd': 'Dfd - Subártico extremo',
        'Dwc': 'Dwc - Subártico com inverno seco',
        'Dwd': 'Dwd - Subártico extremo com inverno seco',
        
        // Climas Polares (E)
        'ET': 'ET - Polar de tundra',
        'EF': 'EF - Polar de gelo perpétuo'
      };
      
      return koppenDescricoes[dadosKoppen.koppen] || `${dadosKoppen.koppen} - Classificação não catalogada`;
    }
    
    return null;
  };

  const toggleSecao = (secao) => {
    setExpandido(prev => ({ ...prev, [secao]: !prev[secao] }));
  };

  const InfoCard = ({ titulo, icone, children, secao }) => (
    <section className={`plot-info-card plot-info-card-${secao}${expandido[secao] ? ' is-open' : ''}`}>
      <button
        type="button"
        className="plot-info-card-toggle"
        onClick={() => toggleSecao(secao)}
        aria-expanded={expandido[secao]}
      >
        <span className="plot-info-card-title"><span className="plot-info-card-icon">{icone}</span><span>{titulo}</span></span>
        <span className="plot-info-card-chevron" aria-hidden="true">⌄</span>
      </button>
      {expandido[secao] && (
        <div className="plot-info-card-content">
          {children}
        </div>
      )}
    </section>
  );

  const InfoRow = ({ label, valor, icone }) => (
    <div className="plot-info-row">
      <span className="plot-info-row-icon" aria-hidden="true">{icone || '•'}</span>
      <span className="plot-info-row-label">{label}</span>
      <strong className="plot-info-row-value">{valor}</strong>
    </div>
  );

  const temCoordenadas = talhao?.coordenada_x && talhao?.coordenada_y;

  return (
    <div className={`plot-info-workspace${darkMode ? ' is-dark' : ''}`}>
      <header className="plot-info-hero">
        <div className="plot-info-hero-copy">
          <span>Ficha técnica</span>
          <h3>Informações do talhão</h3>
          <p>Características cadastradas, localização e referências climáticas reunidas em um só lugar.</p>
        </div>
        <div className="plot-info-hero-tags" aria-label="Resumo rápido do talhão">
          <span><small>Cultura</small><strong>{talhao?.cultura || 'Não cadastrada'}</strong></span>
          <span><small>Área</small><strong>{(talhao?.area || talhao?.area_ha) ? `${talhao.area || talhao.area_ha} ha` : 'Não cadastrada'}</strong></span>
          <span><small>Localização</small><strong>{talhao?.municipio || talhao?.propriedade_municipio || 'Não cadastrada'}</strong></span>
        </div>
      </header>

      {!temCoordenadas && (
        <div className="plot-info-warning" style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: isMobile ? 6 : 8,
          padding: isMobile ? 12 : 16,
          marginBottom: isMobile ? 12 : 16,
          color: '#856404',
          fontSize: isMobile ? 13 : 14,
          lineHeight: 1.5
        }}>
          ⚠️ <strong>Coordenadas não cadastradas.</strong> Para visualizar informações baseadas em localização,
          edite o talhão e adicione as coordenadas geográficas.
        </div>
      )}

      <div className="plot-info-primary-grid">
      {/* RESUMO DO TALHÃO */}
      <InfoCard titulo="Resumo do Talhão" icone="📋" secao="resumo">
        <InfoRow label="Nome" valor={talhao?.nome || 'Não cadastrado'} icone="🏷️" />
        <InfoRow label="Área" valor={(talhao?.area || talhao?.area_ha) ? `${talhao.area || talhao.area_ha} ha` : 'Não cadastrado'} icone="📏" />
        <InfoRow label="Cultura" valor={talhao?.cultura || 'Não cadastrado'} icone="🌿" />
        {talhao?.data_plantio && (
          <InfoRow label="Data de Plantio" valor={new Date(talhao.data_plantio.length === 10 ? talhao.data_plantio + 'T12:00:00' : talhao.data_plantio).toLocaleDateString('pt-BR')} icone="📅" />
        )}
        {talhao?.safra && (
          <InfoRow label="Safra" valor={String(talhao.safra)} icone="🗓️" />
        )}
      </InfoCard>

      {/* GEOLOCALIZAÇÃO */}
      <InfoCard titulo="Geolocalização" icone="🗺️" secao="geolocalizacao">
        <InfoRow
          label="Latitude"
          valor={temCoordenadas ? `${parseFloat(talhao.coordenada_y).toFixed(6)}°` : 'Não cadastrado'}
          icone="📍"
        />
        <InfoRow
          label="Longitude"
          valor={temCoordenadas ? `${parseFloat(talhao.coordenada_x).toFixed(6)}°` : 'Não cadastrado'}
          icone="📍"
        />
        <InfoRow
          label="Altitude"
          valor={loadingAltitude ? 'Carregando...' : altitude ? `${altitude} metros` : 'Não disponível'}
          icone="⛰️"
        />
        <InfoRow
          label="Município"
          valor={talhao?.municipio || talhao?.propriedade_municipio || 'Não cadastrado'}
          icone="🏙️"
        />
        <InfoRow
          label="Estado"
          valor={talhao?.estado || talhao?.propriedade_estado || 'Não cadastrado'}
          icone="🗺️"
        />
      </InfoCard>
      </div>

      {/* CLIMA */}
      <InfoCard titulo="Informações Climáticas" icone="🌡️" secao="clima">
        {(loadingClima || loadingKoppen) && (
          <div style={{ textAlign: 'center', padding: 20, color: '#1976d2' }}>
            ⏳ Carregando dados climáticos...
          </div>
        )}

        {!loadingClima && erroClima && (
          <div className="plot-info-climate-error" role="alert">
            <div>
              <strong>Histórico climático temporariamente indisponível</strong>
              <span>{erroClima}</span>
            </div>
            <button type="button" onClick={() => setTentativaClima(valor => valor + 1)}>
              Tentar novamente
            </button>
          </div>
        )}
        
        {!loadingClima && !loadingKoppen && (dadosClimaticos || dadosKoppen) && (
          <>
            <div className="plot-info-climate-grid" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 12,
              marginBottom: 12
            }}>
              <div className="plot-info-metric is-temperature" style={{
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                borderRadius: 8,
                padding: 12,
                border: '2px solid #ff9800'
              }}>
                <div className="plot-info-metric-label" style={{ fontSize: isMobile ? 11 : 12, color: '#e65100', fontWeight: 600 }}>🌡️ TEMPERATURA MÉDIA ANUAL</div>
                <div className="plot-info-metric-value" style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#f57c00' }}>
                  {dadosClimaticos?.tempMediaAnual || '-'}°C
                </div>
              </div>
              
              <div className="plot-info-metric is-rain" style={{
                background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)',
                borderRadius: 8,
                padding: 12,
                border: '2px solid #03a9f4'
              }}>
                <div className="plot-info-metric-label" style={{ fontSize: isMobile ? 11 : 12, color: '#01579b', fontWeight: 600 }}>🌧️ PRECIPITAÇÃO ANUAL</div>
                <div className="plot-info-metric-value" style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#0277bd' }}>
                  {dadosClimaticos?.precipAnual || '-'} mm
                </div>
              </div>
              
              <div className="plot-info-metric is-cold" style={{
                background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)',
                borderRadius: 8,
                padding: 12,
                border: '2px solid #e91e63'
              }}>
                <div className="plot-info-metric-label" style={{ fontSize: isMobile ? 11 : 12, color: '#880e4f', fontWeight: 600 }}>❄️ MÊS MAIS FRIO</div>
                <div className="plot-info-metric-value" style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#c2185b' }}>
                  {dadosClimaticos?.mesMaisFrio ? 
                    `${dadosClimaticos.mesMaisFrio.nome} - ${dadosClimaticos.mesMaisFrio.temp}°C` : 
                    '-'
                  }
                </div>
              </div>
              
              <div className="plot-info-metric is-hot" style={{
                background: 'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)',
                borderRadius: 8,
                padding: 12,
                border: '2px solid #fbc02d'
              }}>
                <div className="plot-info-metric-label" style={{ fontSize: isMobile ? 11 : 12, color: '#f57f17', fontWeight: 600 }}>☀️ MÊS MAIS QUENTE</div>
                <div className="plot-info-metric-value" style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#f9a825' }}>
                  {dadosClimaticos?.mesMaisQuente ? 
                    `${dadosClimaticos.mesMaisQuente.nome} - ${dadosClimaticos.mesMaisQuente.temp}°C` : 
                    '-'
                  }
                </div>
              </div>
              
              <div className="plot-info-metric is-humidity" style={{
                background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
                borderRadius: 8,
                padding: 12,
                border: '2px solid #26a69a'
              }}>
                <div className="plot-info-metric-label" style={{ fontSize: isMobile ? 11 : 12, color: '#004d40', fontWeight: 600 }}>💧 UMIDADE RELATIVA MÉDIA</div>
                <div className="plot-info-metric-value" style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#00796b' }}>
                  {dadosClimaticos?.umidadeMedia || '-'}%
                </div>
              </div>
            </div>
            
            {dadosClimaticos && (
              <div className="plot-info-source-note" style={{
                background: '#e3f2fd',
                borderRadius: 6,
                padding: isMobile ? 8 : 10,
                marginTop: 12,
                fontSize: isMobile ? 11 : 12,
                color: '#01579b',
                borderLeft: '4px solid #2196f3'
              }}>
                📈 <strong>Dados climáticos</strong> - NASA POWER - Normal climatológica 1991–2020
              </div>
            )}
            
            {obterClassificacaoClimatica() && (
              <InfoRow
                label="Classificação Climática (Köppen)"
                valor={obterClassificacaoClimatica()}
                icone="🌍"
              />
            )}
            
            {dadosKoppen?.koppen && (
              <div className="plot-info-source-note is-koppen" style={{
                background: '#e8f5e9',
                borderRadius: 6,
                padding: isMobile ? 8 : 10,
                marginTop: 12,
                fontSize: isMobile ? 11 : 12,
                color: '#2e7d32',
                borderLeft: '4px solid #4caf50'
              }}>
                📊 <strong>Fonte:</strong> Classificação climática de Köppen-Geiger (<a href="https://doi.org/10.1127/0941-2948/2013/0507" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>Alvares et al. 2013</a>)
              </div>
            )}
          </>
        )}
        
        {!loadingClima && !dadosClimaticos && !dadosKoppen && temCoordenadas && (
          <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>
            Expanda esta seção para carregar dados climáticos
          </div>
        )}
        
        {!temCoordenadas && !dadosKoppen && (
          <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>
            Cadastre as coordenadas do talhão para visualizar dados climáticos
          </div>
        )}
        
        {!loadingKoppen && !dadosKoppen && codeMuni && (
          <div style={{
            background: '#fff3cd',
            borderRadius: 6,
            padding: isMobile ? 10 : 12,
            marginBottom: 12,
            fontSize: isMobile ? 12 : 13,
            color: '#856404',
            borderLeft: '4px solid #ffc107'
          }}>
            ⚠️ Dados climáticos de Köppen não disponíveis para este município
          </div>
        )}
        
        <div className="plot-info-eto" style={{
          background: '#f8f9fa',
          borderRadius: isMobile ? 6 : 8,
          padding: isMobile ? 10 : 12,
          marginTop: isMobile ? 10 : 12
        }}>
          <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 8, fontSize: isMobile ? 14 : 15 }}>
            📈 ETO de Projeto
          </div>
          <p style={{ fontSize: isMobile ? 12 : 13, color: '#666', margin: 0, lineHeight: 1.6 }}>
            A <strong>Evapotranspiração de Referência (ETO) de projeto</strong> representa a demanda 
            atmosférica máxima esperada com 80% de probabilidade de ocorrência. Este valor é fundamental 
            para o dimensionamento de sistemas de irrigação (<a href="https://locus.ufv.br/items/c769c9e5-035f-4ae9-bc6d-decaef4aa9e1" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>Dias, 2018</a>).
          </p>
          <div className="plot-info-eto-value" style={{
            marginTop: isMobile ? 10 : 12,
            padding: isMobile ? 8 : 10,
            background: '#e3f2fd',
            borderRadius: 6,
            borderLeft: '4px solid #1976d2'
          }}>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#666' }}>Valor de Projeto (80% prob.)</div>
            <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#1976d2' }}>
              {loadingEto ? 'Carregando...' : etoProjeto ? `${etoProjeto} mm/dia` : 'Não disponível'}
            </div>
          </div>
          <p style={{ fontSize: isMobile ? 11 : 12, color: '#888', marginTop: 8, marginBottom: 0 }}>
            💡 <em>Valor baseado no ponto mais próximo das coordenadas do talhão</em>
          </p>
        </div>
      </InfoCard>

      {/* ZONEAMENTO */}
      <InfoCard titulo="Zoneamento Agrícola" icone="🌱" secao="zoneamento">
        <div className="plot-info-zarc" style={{
          background: '#f8f9fa',
          borderRadius: isMobile ? 6 : 8,
          padding: isMobile ? 10 : 12
        }}>
          <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 8, fontSize: isMobile ? 14 : 15 }}>
            📅 ZARC - Zoneamento Agrícola de Risco Climático
          </div>
          
          {loadingZARC ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 20, 
              color: '#666',
              fontSize: isMobile ? 12 : 13 
            }}>
              <div style={{ marginBottom: 8 }}>🔄 Carregando dados do ZARC...</div>
            </div>
          ) : dadosZARC ? (
            <>
              {/* Cultura atual */}
              {talhao?.cultura && (
                <div style={{
                  marginBottom: isMobile ? 12 : 16,
                  padding: isMobile ? 10 : 12,
                  background: '#e8f5e9',
                  borderRadius: 6,
                  borderLeft: '4px solid #4caf50'
                }}>
                  <div style={{ fontSize: isMobile ? 11 : 12, color: '#666' }}>Cultura</div>
                  <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#2e7d32' }}>
                    {talhao.cultura}
                  </div>
                </div>
              )}

              {/* Melhor Período de Plantio - DESTAQUE */}
              {dadosZARC.melhorPeriodo && (
                <div style={{
                  marginBottom: isMobile ? 12 : 16,
                  padding: isMobile ? 12 : 16,
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                  color: 'white'
                }}>
                  <div style={{ 
                    fontSize: isMobile ? 11 : 12, 
                    fontWeight: 600,
                    opacity: 0.9,
                    marginBottom: 4
                  }}>
                    🌟 MELHOR PERÍODO DE PLANTIO
                  </div>
                  <div style={{ 
                    fontSize: isMobile ? 20 : 24, 
                    fontWeight: 700,
                    marginBottom: 4
                  }}>
                    {dadosZARC.melhorPeriodo.descricao}
                  </div>
                  <div style={{ 
                    fontSize: isMobile ? 11 : 12,
                    opacity: 0.9
                  }}>
                    📅 Decêndio {dadosZARC.melhorDecendio} de 36 • 🎯 Risco {dadosZARC.melhorRisco} (menor)
                  </div>
                </div>
              )}

              {/* Períodos Alternativos */}
              {(dadosZARC.mesesIdeais || dadosZARC.mesesAdequados) && (
                <div style={{
                  marginBottom: isMobile ? 12 : 16,
                  padding: isMobile ? 10 : 12,
                  background: '#fff',
                  borderRadius: 6,
                  border: '1px solid #e0e0e0'
                }}>
                  {dadosZARC.mesesIdeais && (
                    <div style={{ marginBottom: dadosZARC.mesesAdequados ? 8 : 0 }}>
                      <span style={{ fontSize: isMobile ? 11 : 12, color: '#666' }}>
                        🟢 Períodos ideais:
                      </span>
                      <strong style={{ fontSize: isMobile ? 12 : 13, color: '#2e7d32', marginLeft: 6 }}>
                        {dadosZARC.mesesIdeais}
                      </strong>
                    </div>
                  )}
                  {dadosZARC.mesesAdequados && (
                    <div>
                      <span style={{ fontSize: isMobile ? 11 : 12, color: '#666' }}>
                        🟡 Períodos adequados:
                      </span>
                      <strong style={{ fontSize: isMobile ? 12 : 13, color: '#f57c00', marginLeft: 6 }}>
                        {dadosZARC.mesesAdequados}
                      </strong>
                    </div>
                  )}
                </div>
              )}

              {/* Estatísticas de Decêndios */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: isMobile ? 6 : 8,
                marginBottom: isMobile ? 12 : 16
              }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                  padding: isMobile ? 8 : 10,
                  borderRadius: 6,
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#2e7d32' }}>
                    {dadosZARC.totalIdeais}
                  </div>
                  <div style={{ fontSize: isMobile ? 10 : 11, color: '#666', marginTop: 2 }}>
                    Ideais
                  </div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                  padding: isMobile ? 8 : 10,
                  borderRadius: 6,
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#f57c00' }}>
                    {dadosZARC.totalAdequados}
                  </div>
                  <div style={{ fontSize: isMobile ? 10 : 11, color: '#666', marginTop: 2 }}>
                    Adequados
                  </div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                  padding: isMobile ? 8 : 10,
                  borderRadius: 6,
                  textAlign: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#d32f2f' }}>
                    {dadosZARC.totalAtencao}
                  </div>
                  <div style={{ fontSize: isMobile ? 10 : 11, color: '#666', marginTop: 2 }}>
                    Atenção
                  </div>
                </div>
              </div>

              {/* Calendário Visual */}
              <div style={{ 
                background: 'white', 
                padding: isMobile ? 10 : 12, 
                borderRadius: 8,
                marginBottom: isMobile ? 12 : 16
              }}>
                <div style={{ 
                  fontSize: isMobile ? 12 : 13, 
                  fontWeight: 600, 
                  color: '#666', 
                  marginBottom: 10 
                }}>
                  📆 Calendário de Plantio Recomendado
                </div>
                <CalendarioZARC 
                  decendiosIdeais={dadosZARC.decendiosIdeais}
                  decendiosAdequados={dadosZARC.decendiosAdequados}
                  decendiosAtencao={dadosZARC.decendiosAtencao}
                />
              </div>

              {/* Detalhamento dos Decêndios Ideais */}
              {dadosZARC.decendiosIdeais && dadosZARC.totalIdeais > 0 && (
                <div style={{
                  background: '#f1f8e9',
                  padding: isMobile ? 10 : 12,
                  borderRadius: 6,
                  marginBottom: isMobile ? 12 : 16,
                  border: '1px solid #c5e1a5'
                }}>
                  <div style={{ 
                    fontSize: isMobile ? 12 : 13, 
                    fontWeight: 600, 
                    color: '#558b2f',
                    marginBottom: 8
                  }}>
                    🟢 Decêndios Ideais ({dadosZARC.totalIdeais})
                  </div>
                  <DecendiosDetalhados 
                    decendios={dadosZARC.decendiosIdeais}
                    cor="#4caf50"
                    isMobile={isMobile}
                  />
                </div>
              )}

              {/* Detalhamento dos Decêndios Adequados */}
              {dadosZARC.decendiosAdequados && dadosZARC.totalAdequados > 0 && (
                <div style={{
                  background: '#fff8e1',
                  padding: isMobile ? 10 : 12,
                  borderRadius: 6,
                  marginBottom: isMobile ? 12 : 16,
                  border: '1px solid #ffe082'
                }}>
                  <div style={{ 
                    fontSize: isMobile ? 12 : 13, 
                    fontWeight: 600, 
                    color: '#f57c00',
                    marginBottom: 8
                  }}>
                    🟡 Decêndios Adequados ({dadosZARC.totalAdequados})
                  </div>
                  <DecendiosDetalhados 
                    decendios={dadosZARC.decendiosAdequados}
                    cor="#ff9800"
                    isMobile={isMobile}
                  />
                </div>
              )}

              {/* Classificação Geral */}
              <div style={{
                padding: isMobile ? 10 : 12,
                background: 
                  dadosZARC.nivelGeral === 'favoravel' ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' :
                  dadosZARC.nivelGeral === 'moderado' ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)' :
                  'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)',
                borderRadius: 6,
                borderLeft: `4px solid ${
                  dadosZARC.nivelGeral === 'favoravel' ? '#4caf50' :
                  dadosZARC.nivelGeral === 'moderado' ? '#ff9800' :
                  '#fbc02d'
                }`,
                marginBottom: isMobile ? 10 : 12
              }}>
                <div style={{ fontSize: isMobile ? 11 : 12, color: '#666' }}>
                  Classificação Geral
                </div>
                <div style={{ 
                  fontSize: isMobile ? 14 : 16, 
                  fontWeight: 700, 
                  color: 
                    dadosZARC.nivelGeral === 'favoravel' ? '#2e7d32' :
                    dadosZARC.nivelGeral === 'moderado' ? '#e65100' :
                    '#f57f17'
                }}>
                  {dadosZARC.nivelGeral === 'favoravel' && '✅ Região Favorável'}
                  {dadosZARC.nivelGeral === 'moderado' && '⚠️ Região Moderada'}
                  {dadosZARC.nivelGeral === 'restrito' && '⚡ Região Restrita'}
                </div>
                <div style={{ fontSize: isMobile ? 10 : 11, color: '#666', marginTop: 4 }}>
                  {dadosZARC.nivelGeral === 'favoravel' && 'Esta região apresenta condições favoráveis para o cultivo desta cultura na maioria dos períodos.'}
                  {dadosZARC.nivelGeral === 'moderado' && 'Esta região apresenta condições adequadas, mas requer atenção aos períodos de plantio.'}
                  {dadosZARC.nivelGeral === 'restrito' && 'Esta região possui janelas de plantio mais restritas. Planeje com cuidado.'}
                </div>
              </div>

              {/* Informações adicionais */}
              <div style={{
                fontSize: isMobile ? 10 : 11,
                color: '#666',
                lineHeight: 1.5
              }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>ℹ️ Sobre os Decêndios:</strong>
                  <br />
                  Cada mês é dividido em 3 decêndios (períodos de 10 dias). O ano tem 36 decêndios no total.
                </div>
                <div style={{ 
                  padding: 8, 
                  background: '#e3f2fd', 
                  borderRadius: 4,
                  fontSize: isMobile ? 10 : 11
                }}>
                  <strong>🔵 Risco Baixo (20):</strong> Condições ideais para plantio
                  <br />
                  <strong>🟡 Risco Médio (30):</strong> Condições adequadas com monitoramento
                  <br />
                  <strong>🔴 Risco Alto (40):</strong> Requer atenção especial e manejo adequado
                </div>
              </div>

              {/* Link oficial */}
              <p style={{ 
                fontSize: isMobile ? 10 : 11, 
                color: '#888', 
                marginTop: 12, 
                marginBottom: 0, 
                wordBreak: 'break-word',
                textAlign: 'center' 
              }}>
                🌐 Dados baseados no ZARC/MAPA - Safra 2025/2026
              </p>
              <div style={{ marginTop: 14 }}>
                <DisclaimerInfo tipo="zarc" collapsed={false} />
              </div>
            </>
          ) : (
            <>
              {/* Sem dados disponíveis */}
              <p style={{ fontSize: isMobile ? 12 : 13, color: '#666', margin: '8px 0', lineHeight: 1.6 }}>
                O <strong>Zoneamento Agrícola de Risco Climático (ZARC)</strong> do MAPA indica os períodos 
                mais adequados para plantio de cada cultura, minimizando riscos climáticos.
              </p>
              
              {talhao?.cultura && (
                <div style={{
                  marginTop: isMobile ? 10 : 12,
                  padding: isMobile ? 10 : 12,
                  background: '#fff3e0',
                  borderRadius: 6,
                  borderLeft: '4px solid #ff9800'
                }}>
                  <div style={{ fontSize: isMobile ? 12 : 13, color: '#e65100', fontWeight: 600 }}>
                    ⚠️ Dados não disponíveis
                  </div>
                  <div style={{ fontSize: isMobile ? 11 : 12, color: '#666', marginTop: 4 }}>
                    Não foram encontrados dados do ZARC para <strong>{talhao.cultura}</strong> neste município.
                  </div>
                  
                  {/* Mostrar culturas disponíveis */}
                  {culturasDisponiveis && culturasDisponiveis.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#666', fontWeight: 600, marginBottom: 6 }}>
                        📋 Culturas disponíveis neste município:
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 4,
                        fontSize: isMobile ? 10 : 11
                      }}>
                        {culturasDisponiveis.slice(0, 15).map((cult, idx) => (
                          <span
                            key={idx}
                            style={{
                              background: '#fff',
                              border: '1px solid #ddd',
                              padding: '3px 8px',
                              borderRadius: 4,
                              color: '#666'
                            }}
                          >
                            {cult}
                          </span>
                        ))}
                        {culturasDisponiveis.length > 15 && (
                          <span style={{ color: '#999', fontSize: 10 }}>
                            +{culturasDisponiveis.length - 15} outras
                          </span>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: isMobile ? 10 : 11, 
                        color: '#888', 
                        marginTop: 8,
                        fontStyle: 'italic' 
                      }}>
                        💡 Verifique se o nome da cultura no talhão corresponde exatamente a uma das opções acima.
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <p style={{ fontSize: isMobile ? 11 : 12, color: '#888', marginTop: 12, marginBottom: 0, wordBreak: 'break-word' }}>
                💡 <em>Consulte o ZARC oficial em: <a href="https://www.gov.br/agricultura/pt-br/assuntos/riscos-seguro/programa-nacional-de-zoneamento-agricola-de-risco-climatico" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>gov.br/agricultura</a></em>
              </p>
              <div style={{ marginTop: 14 }}>
                <DisclaimerInfo tipo="zarc" collapsed={false} />
              </div>
            </>
          )}
        </div>
      </InfoCard>

    </div>
  );
}

export default InfoAdicionais;
